import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getAllStaff } from "../../../../../services/staff";
import "../settings.css";

/* ──────────────────────────────────────────────────────────────
   Users & roles panel (redesign) — NEW structure, connects to the
   existing staff data where it exists; everything without a backing
   flow is a clearly-disabled "Coming soon" (never faked).

   CONNECTED (real data + real flows):
   - Seat overview: member count from state.staff.allStaff, remaining
     staff seats from state.dashboard.dashboardCounts.remainingStaffCount,
     plan name from profile.subscription.packageId.name.
   - Members table: real users from state.staff.allStaff (fetched via
     getAllStaff). Name / email / role / facilities / 2FA / last active
     are derived from the real staff record fields.
   - "+ Invite user" and per-row "Manage" route to the EXISTING staff
     management flow (/home/staff/staff/add and /home/staff/:id/view).

   COMING SOON (no existing settings backend — rendered as design
   chrome, visibly disabled, never simulate success):
   - Pending invites (no invite-acceptance backend in settings).
   - Editable role-permission matrix (the capability matrix below is a
     read-only reference, not an editor).

   Admin-only panel (profile.user.type !== "staff").
   ────────────────────────────────────────────────────────────── */

/* Module-access matrix — the REAL per-role permission defaults the app uses
   (utils getInitialAdminPermissions / initialStaffPermissions /
   initialContractorPermissions; labels from PERMISSION_LABELS). Individual
   users are edited per-user from the Members tab → Manage. */
const CAPABILITIES = [
  { cap: "AI Access", admin: "✓", staff: "✓", contractor: "✓" },
  { cap: "Staff", admin: "✓", staff: "—", contractor: "✓" },
  { cap: "Task", admin: "✓", staff: "✓", contractor: "✓" },
  { cap: "Notes", admin: "✓", staff: "✓", contractor: "✓" },
  { cap: "Medication", admin: "✓", staff: "✓", contractor: "✓" },
  { cap: "Facility", admin: "✓", staff: "—", contractor: "✓" },
  { cap: "Invoice", admin: "✓", staff: "—", contractor: "✓" },
  { cap: "Billing", admin: "✓", staff: "—", contractor: "✓" },
  { cap: "Medication Management", admin: "—", staff: "Optional", contractor: "—" },
];

/* ── helpers (derive display values from a real staff record) ── */
const fullName = (s) => {
  const fn = s?.firstName || (s?.name ? s.name.split(" ")[0] : "");
  const ln =
    s?.lastName ||
    (s?.name ? s.name.split(" ").slice(1).join(" ") : "");
  return `${fn || ""} ${ln || ""}`.trim() || s?.email || "Unnamed user";
};

const initials = (s) => {
  const n = fullName(s);
  const parts = n.split(" ").filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0][0] + (parts[1] ? parts[1][0] : "")).toUpperCase();
};

const facilitiesLabel = (s) => {
  const list = (s?.facilityId || [])
    .map((f) => f?.name)
    .filter(Boolean);
  if (!list.length) return "—";
  if (list.length > 2) return `${list.length} facilities`;
  return list.join(", ");
};

/* Role badge — Administrator = info, everyone else = neutral. */
const roleBadge = (s) => {
  const role = (s?.role || s?.position || s?.type || "").toString();
  const lc = role.toLowerCase();
  if (lc === "admin" || lc === "administrator") {
    return { className: "badge-info", label: "Administrator" };
  }
  if (lc === "contractor") {
    return { className: "badge-warn", label: "Contractor" };
  }
  const label = role
    ? role.charAt(0).toUpperCase() + role.slice(1)
    : "Staff";
  return { className: "badge-neutral", label };
};

/* 2FA badge — real fields on the user/staff record. */
const twoFaBadge = (s) => {
  const verified = s?.twoFactorVerified === true;
  const sms = s?.sms2FaEnabled === true;
  const email = s?.email2FaEnabled === true;
  const access = s?.is2FaAccess === true;
  if (verified && (sms || email)) {
    return { className: "badge-success", label: "Enabled" };
  }
  if (access && sms && !email) {
    return { className: "badge-warn", label: "SMS only" };
  }
  if (access && (sms || email)) {
    return { className: "badge-warn", label: "Not verified" };
  }
  return { className: "badge-neutral", label: "Off" };
};

const lastActive = (s) => {
  const raw = s?.lastLogin || s?.lastActive || s?.updatedAt;
  if (!raw) return "—";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return "—";
  const now = Date.now();
  const diffMs = now - d.getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 2) return "Now";
  if (min < 60) return `${min}m ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const invitedOn = (s) => {
  const raw = s?.createdAt;
  if (!raw) return "—";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export default function UsersPanel({ profile, role }) {
  const navigate = useNavigate();

  // Admin-only panel. Mirror the gating used across Settings:
  // admin === profile.user.type !== "staff".
  const isAdmin =
    role === "admin" ||
    (profile?.user?.type ? profile.user.type !== "staff" : true);

  const staffState = useSelector((state) => state?.staff);
  const staffLoading = staffState?.loading;
  // /staff/all returns `staffs` (active staff + admin) and `allStaff`
  // (everyone under this admin — any type, incl. deleted).
  const activeStaffs = staffState?.staff?.staffs || [];
  const everyone = staffState?.allStaff || [];
  const dashboardCounts = useSelector(
    (state) => state?.dashboard?.dashboardCounts
  );

  const [activeTab, setActiveTab] = useState("members");

  // Pull real staff/contractor records once.
  useEffect(() => {
    if (isAdmin) getAllStaff("all", false);
  }, [isAdmin]);

  const isContractor = (s) => (s?.type || "").toLowerCase() === "contractor";

  // Members = staff (excludes contractors). Contractors get their own tab.
  const members = useMemo(
    () =>
      [...activeStaffs]
        .filter((s) => !isContractor(s))
        .sort((a, b) => fullName(a).localeCompare(fullName(b))),
    [activeStaffs]
  );
  const contractors = useMemo(
    () =>
      [...everyone]
        .filter((s) => isContractor(s) && s?.deleted !== true)
        .sort((a, b) => fullName(a).localeCompare(fullName(b))),
    [everyone]
  );
  // Pending invites = invited staff who haven't accepted yet
  // (inactive + email not verified, not deleted, not contractors).
  const pendingInvites = useMemo(
    () =>
      [...everyone]
        .filter(
          (s) =>
            !isContractor(s) &&
            s?.deleted !== true &&
            !s?.active &&
            !s?.isEmailVerified
        )
        .sort(
          (a, b) =>
            new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0)
        ),
    [everyone]
  );
  const pendingCount = pendingInvites.length;

  // Seat overview — real data only.
  const memberCount = members.length;
  const contractorCount = contractors.length;
  const remainingStaffSeats =
    typeof dashboardCounts?.remainingStaffCount === "number"
      ? dashboardCounts.remainingStaffCount
      : null;
  const planName = profile?.subscription?.packageId?.name || null;
  const totalSeats =
    remainingStaffSeats != null ? memberCount + remainingStaffSeats : null;

  const goInvite = () => navigate("/home/staff/staff/add");
  const goManage = (s) => {
    if (s?.id) navigate(`/home/staff/${s.id}/view`);
  };

  // Shared row renderer for the Members and Contractors tables.
  const renderTableBody = (list, { showManage, emptyText }) => {
    if (staffLoading && !list.length) {
      return (
        <tr>
          <td colSpan={6} style={{ color: "var(--muted)" }}>
            Loading…
          </td>
        </tr>
      );
    }
    if (!list.length) {
      return (
        <tr>
          <td colSpan={6} style={{ color: "var(--muted)" }}>
            {emptyText}
          </td>
        </tr>
      );
    }
    return list.map((s, i) => {
      const rb = roleBadge(s);
      const fb = twoFaBadge(s);
      return (
        <tr key={s?.id || s?._id || i}>
          <td>
            <div className="person-cell">
              <div className="avatar-md">{initials(s)}</div>
              <div>
                <div className="person-name">{fullName(s)}</div>
                <div className="person-sub">{s?.email || "—"}</div>
              </div>
            </div>
          </td>
          <td>
            <span className={`badge ${rb.className}`}>{rb.label}</span>
          </td>
          <td>{facilitiesLabel(s)}</td>
          <td>
            <span className={`badge ${fb.className}`}>{fb.label}</span>
          </td>
          <td>{lastActive(s)}</td>
          <td style={{ textAlign: "right" }}>
            {showManage ? (
              <button className="btn sm" type="button" onClick={() => goManage(s)}>
                Manage
              </button>
            ) : null}
          </td>
        </tr>
      );
    });
  };

  // Staff users shouldn't reach this panel (shell role-gates it), but
  // guard defensively so it never renders admin-only seat data for them.
  if (!isAdmin) {
    return (
      <div className="settings-panel active">
        <div className="page-header">
          <div>
            <div className="page-title">Users &amp; roles</div>
            <div className="page-subtitle">
              Only administrators can manage users and roles.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-panel active">
      {/* ── Header ── */}
      <div className="page-header">
        <div>
          <div className="page-title">Users &amp; roles</div>
          <div className="page-subtitle">
            Manage who has access and what they can do.
          </div>
        </div>
        <div className="page-actions">
          {/* Routes to the EXISTING add-staff flow — real action. */}
          <button className="btn primary" type="button" onClick={goInvite}>
            + Invite user
          </button>
        </div>
      </div>

      {/* ── Seat overview (real data) ── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="info-grid" style={{ padding: "18px 24px" }}>
          <div className="info-row">
            <div className="info-label">Staff</div>
            <div className="info-value">{memberCount}</div>
          </div>
          <div className="info-row">
            <div className="info-label">Seats available</div>
            <div className="info-value">
              {remainingStaffSeats != null ? remainingStaffSeats : "—"}
            </div>
          </div>
          <div className="info-row">
            <div className="info-label">Plan</div>
            <div className="info-value">
              {planName || "—"}
              {totalSeats != null && (
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: "var(--muted)",
                    marginLeft: 6,
                  }}
                >
                  · {memberCount}/{totalSeats} seats used
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Sub-tabs ── */}
      <div className="tabs">
        <button
          type="button"
          className={`tab ${activeTab === "members" ? "active" : ""}`}
          onClick={() => setActiveTab("members")}
        >
          Staff ({memberCount})
        </button>
        <button
          type="button"
          className={`tab ${activeTab === "contractors" ? "active" : ""}`}
          onClick={() => setActiveTab("contractors")}
        >
          Contractors ({contractorCount})
        </button>
        <button
          type="button"
          className={`tab ${activeTab === "pending" ? "active" : ""}`}
          onClick={() => setActiveTab("pending")}
        >
          Pending invites ({pendingCount})
        </button>
        <button
          type="button"
          className={`tab ${activeTab === "roles" ? "active" : ""}`}
          onClick={() => setActiveTab("roles")}
        >
          Roles
        </button>
      </div>

      {/* ── Members (real users) ── */}
      {activeTab === "members" && (
        <div className="card">
          {/* .table-scroll keeps the six columns readable on a phone by
              scrolling the table sideways instead of squeezing it. */}
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  <th>Facilities</th>
                  <th>2FA</th>
                  <th>Last active</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {renderTableBody(members, {
                  showManage: true,
                  emptyText: "No team members yet. Use “+ Invite user” to add one.",
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Contractors (real users, type = contractor) ── */}
      {activeTab === "contractors" && (
        <div className="card">
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr>
                  <th>Contractor</th>
                  <th>Role</th>
                  <th>Facilities</th>
                  <th>2FA</th>
                  <th>Last active</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {renderTableBody(contractors, {
                  showManage: false,
                  emptyText: "No contractors linked to this organization yet.",
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Pending invites — staff invited but not yet accepted ── */}
      {activeTab === "pending" && (
        <div className="card">
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr>
                  <th>Invitee</th>
                  <th>Role</th>
                  <th>Invited</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {staffLoading && !pendingInvites.length ? (
                  <tr>
                    <td colSpan={4} style={{ color: "var(--muted)" }}>
                      Loading…
                    </td>
                  </tr>
                ) : pendingInvites.length ? (
                  pendingInvites.map((s, i) => {
                    const rb = roleBadge(s);
                    return (
                      <tr key={s?.id || s?._id || i}>
                        <td>
                          <div className="person-cell">
                            <div className="avatar-md">{initials(s)}</div>
                            <div>
                              <div className="person-name">{fullName(s)}</div>
                              <div className="person-sub">{s?.email || "—"}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className={`badge ${rb.className}`}>{rb.label}</span>
                        </td>
                        <td>{invitedOn(s)}</td>
                        <td>
                          <span className="badge badge-warn">Awaiting acceptance</span>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={4} style={{ color: "var(--muted)" }}>
                      No pending invites. Use “+ Invite user” to add a team member.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Roles — real per-role module-access defaults ── */}
      {activeTab === "roles" && (
        <div className="card">
          <div className="card-section">
            <div className="card-title">
              Role permissions{" "}
              <span className="badge badge-neutral" style={{ marginLeft: 6 }}>
                Coming soon
              </span>
            </div>
            <div className="card-subtitle" style={{ marginBottom: 14 }}>
              Default module access per role. Creating and editing custom roles
              is coming soon — for now, to change a specific user&apos;s access,
              open them from the Members tab and edit their permissions.
            </div>
            <div className="table-scroll">
              <table className="data">
                <thead>
                  <tr>
                    <th>Module</th>
                    <th>Administrator</th>
                    <th>Staff</th>
                    <th>Contractor</th>
                  </tr>
                </thead>
                <tbody>
                  {CAPABILITIES.map((c) => (
                    <tr key={c.cap}>
                      <td>{c.cap}</td>
                      <td>{c.admin}</td>
                      <td>{c.staff}</td>
                      <td>{c.contractor}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="card-footer">
            <button type="button" className="btn primary" disabled>
              Create role
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
